import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  class: "dialogue-minimap",
  "aria-label": "对话流总览"
}
const _hoisted_2 = ["aria-expanded"]
const _hoisted_3 = { "aria-hidden": "true" }
const _hoisted_4 = ["viewBox"]
const _hoisted_5 = ["transform"]
const _hoisted_6 = ["d"]
const _hoisted_7 = ["x", "y", "width", "height"]
const _hoisted_8 = ["x", "y", "width", "height"]

import { computed, ref } from "vue";
import type { TextPreviewLayout } from "../utils/dialogueTextPreviewLayout";
import type { TextPreviewBlock } from "../utils/dialogueTextPreview";
import { clipMinimapViewport, fitDialogueMinimap, minimapPointToCanvas, type MinimapRect } from "../utils/dialogueMinimap";

const mapWidth = 184, mapHeight = 124;

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogueMinimap',
  props: {
    layout: {},
    blocks: {},
    viewport: {},
    activeBlockId: {},
    zoom: {}
  },
  emits: ["navigate"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const expanded = ref(true);
const dragging = ref(false);
const projection = computed(() => fitDialogueMinimap(props.layout.width, props.layout.height, mapWidth, mapHeight));
const visible = computed(() => clipMinimapViewport(props.viewport, props.layout.width, props.layout.height));
const frame = computed(() => ({
  x: projection.value.x + visible.value.x * projection.value.scale,
  y: projection.value.y + visible.value.y * projection.value.scale,
  width: visible.value.width * projection.value.scale,
  height: visible.value.height * projection.value.scale,
}));
const kinds = computed(() => new Map(props.blocks.map(block => [block.id, block.kind])));
let drag: { pointerId: number; offsetX: number; offsetY: number } | undefined;

function point(event: PointerEvent) {
  const rect = (event.currentTarget as SVGSVGElement).getBoundingClientRect();
  return minimapPointToCanvas((event.clientX - rect.left) * mapWidth / rect.width,
    (event.clientY - rect.top) * mapHeight / rect.height, projection.value);
}
function start(event: PointerEvent) {
  if (event.button !== 0 || drag) return;
  const position = point(event);
  const view = props.viewport;
  const inside = position.x >= visible.value.x && position.x <= visible.value.x + visible.value.width &&
    position.y >= visible.value.y && position.y <= visible.value.y + visible.value.height;
  drag = { pointerId: event.pointerId, offsetX: inside ? position.x - view.x : view.width / 2,
    offsetY: inside ? position.y - view.y : view.height / 2 };
  dragging.value = true;
  (event.currentTarget as SVGSVGElement).setPointerCapture(event.pointerId);
  if (!inside) move(event);
}
function move(event: PointerEvent) {
  if (!drag || drag.pointerId !== event.pointerId) return;
  const position = point(event);
  emit("navigate", { x: position.x - drag.offsetX, y: position.y - drag.offsetY });
}
function end(event: PointerEvent) {
  if (!drag || drag.pointerId !== event.pointerId) return;
  drag = undefined;
  dragging.value = false;
  const svg = event.currentTarget as SVGSVGElement;
  if (svg.hasPointerCapture(event.pointerId)) svg.releasePointerCapture(event.pointerId);
}
function keydown(event: KeyboardEvent) {
  const directions: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
  const direction = directions[event.key];
  if (!direction) return;
  event.preventDefault();
  emit("navigate", { x: props.viewport.x + direction[0] * props.viewport.width * .2,
    y: props.viewport.y + direction[1] * props.viewport.height * .2 });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("aside", _hoisted_1, [
    _createElementVNode("button", {
      type: "button",
      class: "minimap-heading",
      "aria-expanded": expanded.value,
      onClick: _cache[0] || (_cache[0] = ($event: any) => (expanded.value = !expanded.value))
    }, [
      _cache[1] || (_cache[1] = _createElementVNode("span", null, "对话总览", -1)),
      _createElementVNode("small", null, _toDisplayString(Math.round(__props.zoom * 100)) + "%", 1),
      _createElementVNode("span", _hoisted_3, _toDisplayString(expanded.value ? '−' : '＋'), 1)
    ], 8, _hoisted_2),
    (expanded.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (_openBlock(), _createElementBlock("svg", {
            class: _normalizeClass(["minimap-canvas", { dragging: dragging.value }]),
            viewBox: `0 0 ${mapWidth} ${mapHeight}`,
            tabindex: "0",
            role: "group",
            "aria-label": "对话流缩略图，点击定位或拖动蓝框，方向键移动视野",
            onPointerdown: _withModifiers(start, ["stop","prevent"]),
            onPointermove: _withModifiers(move, ["stop"]),
            onPointerup: _withModifiers(end, ["stop"]),
            onPointercancel: end,
            onLostpointercapture: end,
            onKeydown: _withModifiers(keydown, ["stop"])
          }, [
            _createElementVNode("g", {
              transform: `translate(${projection.value.x} ${projection.value.y}) scale(${projection.value.scale})`,
              "aria-hidden": "true"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.layout.edges, (edge) => {
                return (_openBlock(), _createElementBlock("path", {
                  key: edge.id,
                  d: edge.path,
                  class: _normalizeClass(["minimap-edge", { 'return-edge': edge.isReturn }])
                }, null, 10, _hoisted_6))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.layout.blocks, (block) => {
                return (_openBlock(), _createElementBlock("rect", {
                  key: block.id,
                  x: block.x,
                  y: block.y,
                  width: block.width,
                  height: block.height,
                  rx: "8",
                  class: _normalizeClass(["minimap-block", [`kind-${kinds.value.get(block.id)}`, { active: block.id === __props.activeBlockId }]])
                }, null, 10, _hoisted_7))
              }), 128))
            ], 8, _hoisted_5),
            _createElementVNode("rect", {
              x: frame.value.x,
              y: frame.value.y,
              width: frame.value.width,
              height: frame.value.height,
              rx: "2",
              class: "minimap-viewport",
              "aria-hidden": "true"
            }, null, 8, _hoisted_8)
          ], 42, _hoisted_4)),
          _cache[2] || (_cache[2] = _createElementVNode("p", null, "拖动蓝框或点击定位", -1))
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})